export default {
    chapters: {
        problem: "Problema",
        solution: "Solución",
        features: "Funciones",
        world: "Ruta IA",
        pricing: "Precios",
        cta: "Activar",
    },
    hero: {
        badge: "AI Workforce para la logística",
        title: "Deja de perseguir correos. Empieza a pilotar los envíos.",
        description:
            "EveMind unifica correos, documentos, seguimiento y operaciones en un solo flujo de ejecución impulsado por IA.",
        titleLine1: "Deja de perseguir correos.",
        titleLine2: "Empieza a pilotar los envíos.",
        typewriterPrefix: "Empieza a",
        typewriterSuffixes: [
            "pilotar los envíos.",
            "reducir retrasos.",
            "proteger márgenes.",
            "alinear cada handoff.",
            "actuar antes de que llegue el riesgo.",
        ],
        subtitle:
            "EveMind unifica correos, documentos, seguimiento y operaciones en un solo flujo de ejecución impulsado por IA.",
        imageAlt: "Buque portacontenedor en alta mar — transporte marítimo y logística global",
        ctaPrimary: "Activar cuenta",
        ctaExplore: "Ver funciones",
        ctaLogin: "Iniciar sesión",
        ctaGoToApp: "Ir al área privada",
        ctaContact: "Contáctanos",
    },
    problem: {
        label: "El problema",
        title: "Operaciones fragmentadas, decisiones lentas, márgenes en tensión.",
        description:
            "Entre hilos interminables, adjuntos dispersos y actualizaciones manuales, los equipos logísticos reaccionan demasiado tarde en lugar de anticipar el riesgo.",
        cards: {
            fragmented: {
                title: "Datos repartidos en canales desconectados",
                description: "Correo, Excel, PDF y chat no comparten un contexto operativo único.",
            },
            late: {
                title: "Las alertas llegan cuando el impacto ya está en marcha",
                description: "Las incidencias críticas aparecen cuando los costes y retrasos ya están activos.",
            },
            manual: {
                title: "Demasiado trabajo manual repetitivo",
                description: "El equipo dedica tiempo a actualizar estados en lugar de impulsar resultados.",
            },
        },
    },
    solution: {
        label: "Solución EveMind",
        title: "Una consola de IA que ve antes, decide más rápido y ejecuta mejor.",
        description:
            "EveMind crea una vista operativa única por envío y activa automáticamente la mejor siguiente acción.",
        bullets: {
            ingest: {
                title: "Ingesta automática multi‑fuente",
                description: "Conecta correos, documentos y eventos de seguimiento en tiempo real.",
            },
            decide: {
                title: "Inteligencia de decisión contextual",
                description: "Prioriza el riesgo y muestra acciones de alto impacto para cada equipo.",
            },
            act: {
                title: "Ejecución asistida por IA",
                description: "Responde, actualiza, valida y coordina sin perder contexto.",
            },
        },
        timelineLabel: "De la señal a la acción",
        timeline: {
            capture: { time: "T+0 min", title: "Nuevo correo y documento capturados" },
            validate: { time: "T+2 min", title: "Cruce automático y detección de anomalías" },
            predict: { time: "T+5 min", title: "Risk score actualizado con explicación" },
            notify: { time: "T+6 min", title: "Alerta y siguiente acción al equipo adecuado" },
        },
    },
    agents: {
        care: {
            title: "Eve Care",
            subtitle: "Agente IA de atención al cliente",
            description:
                "Gestiona solicitudes de clientes, alinea equipos y mantiene el historial conversacional vinculado al contexto vivo del envío.",
            features: [
                "Responde a las solicitudes de los clientes en tiempo real",
                "Actualiza automáticamente a los clientes sobre el estado del envío",
                "Registra todas las interacciones automáticamente",
            ],
        },
        track: {
            title: "Eve Track",
            subtitle: "Agente de seguimiento en tiempo real",
            description:
                "Unifica el tracking multi‑armador, actualiza predicciones de ETA y señala anomalías de ruta antes de que se conviertan en incidencias.",
            features: [
                "Conecta datos de satélite y GPS en tiempo real",
                "Responde automáticamente a las peticiones de actualización de los clientes",
                "Genera notificaciones por retrasos y anomalías",
            ],
        },
        quote: {
            title: "Eve Quote",
            subtitle: "Agente de automatización de cotizaciones",
            description:
                "Lee solicitudes de cotización entrantes, calcula precios con tu lógica y envía ofertas listas para aprobar en minutos.",
            features: [
                "Calcula tarifas en segundos según tus reglas de precio",
                "Genera ofertas profesionales listas para enviar",
                "Gestiona modificaciones y actualizaciones automáticamente",
            ],
        },
        read: {
            title: "Eve Read",
            subtitle: "Agente de lectura de documentos",
            description:
                "Extrae campos clave de documentos comerciales, detecta discrepancies y alimenta los sistemas sin reintroducción manual.",
            features: [
                "Lee documentos en cualquier formato automáticamente",
                "Extrae y estructura datos clave al instante",
                "Envía datos directamente a tu TMS",
            ],
        },
        plan: {
            title: "Eve Plan",
            subtitle: "Agente de planificación de rutas",
            description:
                "Construye planes diarios óptimos, reprioriza al instante ante cambios y mantiene la ejecución alineada con la capacidad.",
            features: [
                "Sugiere los planes de transporte más eficientes cada día",
                "Recalcula prioridades en tiempo real cuando cambian las condiciones",
                "Considera disponibilidad de vehículos y restricciones operativas",
            ],
        },
    },
    features: {
        label: "Cómo lo resolvemos",
        title: "One AI agents fleet, one operational control layer",
        description:
            "Cada agente cubre una fase crítica; juntos reducen errores, retrasos y costes de coordinación.",
        showcase: {
            learnMore: "¿Listo para activar EveMind?",
            care: {
                mockLabel: "Bandeja en vivo",
                messages: [
                    "Cliente: ¿Cuál es el último ETA del MSCU7654321?",
                    "Eve Care: Secuencia de buque actualizada — nuevo ETA 14 abr., 09:40 local.",
                    "Cliente: Cuando podáis, enviad el borrador revisado del B/L.",
                ],
            },
            track: {
                mockLabel: "Flujo de tracking",
                lines: [
                    "Gate out Mombasa · estado OK",
                    "Transbordo Colombo · desviación −6h marcada",
                    "ATD actualizado · feed de línea v2 aplicado",
                ],
            },
            quote: {
                mockLabel: "API de cotización",
                responses: [
                    'HTTP 200 { "quoteId": "q_9f2c4a1b-c3d5-…", "status": "draft_ready" }',
                    'HTTP 200 { "quoteId": "q_9f2c4a1b-c3d5-…", "status": "sent", "margin": 0.124 }',
                ],
            },
            read: {
                mockLabel: "Tubería de extracción",
                chips: ["B/L", "Factura", "Packing list"],
                rows: [
                    { tone: "ok", title: "Factura ↔ Packing list", meta: "Pesos alineados" },
                    { tone: "warn", title: "Shipper en B/L", meta: "Revisión humana en cola" },
                    { tone: "ok", title: "Fitosanitario", meta: "Registrado y archivado" },
                ],
            },
            plan: {
                mockLabel: "Tablero de prioridades",
                rows: [
                    { rank: "P1", text: "Recogida reefer — terminal Génova, franja 07:30" },
                    { rank: "P2", text: "Seguimiento booking — MSC #448821" },
                    { rank: "P3", text: "Paquete doc. — ref. cliente ASH-2026-044" },
                ],
            },
        },
    },
    world: {
        label: "Narrativa 3D",
        title: "Una ruta inteligente que evoluciona al hacer scroll.",
        description:
            "Recorre la ruta de Mombasa a San Francisco: cada parada coincide con una etapa que EveMind cubre en una ruta transoceánica real.",
        stopPrefix: "Parada {{index}}",
        stops: {
            care: {
                title: "Comunicación con el cliente siempre alineada",
                description: "Los clientes reciben actualizaciones claras y contextuales sin perseguir hilos a mano.",
                place: "Mombasa, Kenia — puerto de carga",
            },
            track: {
                title: "Visibilidad de punta a punta en la ruta",
                description: "Tracking multi‑fuente y alertas proactivas por retrasos y desvíos.",
                place: "Colombo, Sri Lanka — tránsito océano Índico",
            },
            quote: {
                title: "Cotizaciones rápidas y coherentes",
                description: "De la solicitud a la oferta en minutos, con la lógica de margen aplicada.",
                place: "Singapur — estrecho de Malaca",
            },
            read: {
                title: "Verificación documental automatizada",
                description: "Cruce y detección de anomalías antes de que el problema cueste dinero.",
                place: "Shanghái, China — puerta del Pacífico",
            },
            plan: {
                title: "Planificación operativa dinámica",
                description: "Prioridades y recursos se adaptan en tiempo real a condiciones cambiantes.",
                place: "San Francisco, EE. UU. — llegada y descarga",
            },
        },
    },
    pricing: {
        label: "Precios",
        title: "Un modelo comercial diseñado en torno a tu operación.",
        description:
            "Sin planes únicos para todos. Ajustamos alcance, integraciones y profundidad de automatización a tu flujo logístico.",
        cards: {
            volume: {
                title: "Basado en volumen",
                description: "Escala con número de envíos, flujo documental e interacciones.",
            },
            operations: {
                title: "Basado en complejidad",
                description: "Alineado con equipos, madurez de SOP y requisitos de SLA.",
            },
            integration: {
                title: "Basado en integración",
                description: "Conexiones TMS/ERP y onboarding calibrados a tu stack.",
            },
        },
        ctaPrimary: "Hablar con ventas",
        ctaSecondary: "Activar cuenta",
    },
    finalCta: {
        title: "¿Listo para convertir la logística en ventaja estratégica?",
        description:
            "Activa EveMind y gestiona riesgos, documentos y tracking en una sola capa operativa impulsada por IA.",
        ctaPrimary: "Activar cuenta",
        ctaSecondary: "Reservar una demo",
    },
    footer: {
        legal: "Legal",
        contactTitle: "¿Listo para transformar tu logística?",
        contactDescription: "Ponte en contacto con nuestro equipo para saber más sobre EveMind.",
        contactButton: "Contáctanos",
        loginLink: "¿Ya tienes una cuenta?",
        loginButton: "Iniciar sesión",
    },
};
